import React, { Component } from "react";
import { getAuth } from "firebase/auth";
import { createSafeURL, showNotification } from "../uti";
import "../css/App.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import themeLarge from "../component/ThemeLarge";
import { compressImage } from "../utils/imageUtils";
import { uploadImageToStorage } from "../utils/uploadImageToStorage";
import { getFirestore, getDoc, setDoc, doc } from "firebase/firestore";

/**
 * Components
 */
import BlogEditor from "../component/createblog/BlogEditor";
import BlogMetadataForm from "../component/createblog/BlogMetadataForm";
import SavePublishButtons from "../component/createblog/SavePublishButtons";
import PublishModal from "../component/createblog/PublishModal";
import {
  fetchBlogData,
  publishBlog,
} from "../component/createblog/blogFirebaseUtils";
import { saveDraftToCache, resetInactivityTimer } from "../utils/autoSaveDraft";
import { loadCachedBlogById } from "../utils/blogEditorLifecycle";
import LastSavedStatus from "../component/createblog/LastSavedStatus";
import { clearAllBlogCache } from "../utils/clearAllBlogCache";

class CreateBlogPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      title: "",
      author: "",
      content: "",
      slug: "",
      excerpt: "",
      createdAt: null,
      updatedAt: null,
      status: "draft", // Default blog status
      timeoutId: null, // Timer for auto-save on inactivity
      hasChanges: false, // Track if changes have been made
      cacheKey: null,
      images: [],
      coverPhoto: null,
      tags: [],
      allTags: [], // From Firestore for suggestions
    };
  }

  componentDidMount() {
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      this.setState({ author: user.email });
    }

    const blogId = new URLSearchParams(window.location.search).get("id");
    if (blogId?.startsWith("cache:")) {
      // possible location for error
      const { data, error } = loadCachedBlogById(blogId);

      if (error === "parse_error") {
        console.warn("Failed to parse cached blog from URL");
        showNotification("Corrupted cached draft!", "error", 3000);
      } else if (error === "not_found") {
        showNotification("Cached draft not found!", "error", 3000);
      } else {
        this.setState(data);
      }
      return;
    } else if (blogId) {
      fetchBlogData(blogId)
        .then((blogData) => {
          this.setState({
            ...blogData,
            images: blogData.photos,
            blogId,
            status: "published",
            hasChanges: false,
          });
        })
        .catch((err) => {
          console.error("Failed to fetch blog from Firestore:", err);
          showNotification("Failed to load published blog!", "error", 3000);
        });
    }
    this.fetchTagSuggestions();
  }

  fetchTagSuggestions = async () => {
    const db = getFirestore();
    const tagDocRef = doc(db, "tags", "all_tags");
    const tagDocSnap = await getDoc(tagDocRef);
    const allTags = tagDocSnap.exists() ? tagDocSnap.data().tags || [] : [];
    this.setState({ allTags });
  };

  // Reset inactivity timer on user activity
  resetInactivityTimer = () => {
    if (this.state.status === "published") return;
    const newTimeout = resetInactivityTimer(this.state.timeoutId, () =>
      saveDraftToCache(this.state, (newState) => this.setState(newState))
    );

    this.setState({ timeoutId: newTimeout });
  };

  handleInputChange = (event) => {
    const { name, value } = event.target;

    const newState = {
      [name]: value,
      hasChanges: true,
    };

    // If title is changing, update the slug too
    if (name === "title") {
      newState.slug = createSafeURL(value);
    }

    this.setState(newState);
    this.resetInactivityTimer(); // Reset inactivity timer on input
  };

  handleContentChange = (value) => {
    // Generate excerpt from the first 30 characters of the content
    if (this.state.status !== "published") {
      const excerpt = value.replace(/<[^>]+>/g, "").slice(0, 30); // Strip HTML tags
      this.setState({
        content: value,
        excerpt,
        hasChanges: true, // Mark as changed
      });
      this.resetInactivityTimer(); // Reset inactivity timer on content change
    } else {
      this.setState({
        content: value,
        hasChanges: true, // Mark as changed
      });
    }
  };

  handlePhotoUpdate = (index, newUrl) => {
    const updatedPhotos = [...this.state.images];
    updatedPhotos[index] = newUrl;
    this.setState({ images: updatedPhotos, hasChanges: true });
    // alert(updatedPhotos);
    // alert(this.state.images);
  };

  handleSave = async (status) => {
    if (this.state.status === "published") {
      // Disable draft saving
      if (status === "draft") {
        showNotification(
          "You cannot save a published blog as draft.",
          "error",
          3000
        );
        return;
      }
    }
    const newUpdatedAt = new Date();
    const blogData = {
      title: this.state.title,
      author: this.state.author,
      content: this.state.content,
      excerpt: this.state.excerpt,
      slug: this.state.slug,
      coverPhoto: this.state.coverPhoto,
      photos: this.state.images,
      createdAt: this.state.createdAt,
      updatedAt: newUpdatedAt.toISOString(),
    };

    if (status === "draft") {
      try {
        const cacheKey = this.state.cacheKey || `cachedDraft_${Date.now()}`;
        localStorage.setItem(cacheKey, JSON.stringify(blogData));

        this.setState({
          cacheKey,
          updatedAt: newUpdatedAt,
          hasChanges: false,
        });

        showNotification("Draft saved to Local Storage!", "success", 3000);
        return;
      } catch (error) {
        console.error("Error saving blog:", error);
        showNotification("An error occurred! Check Log!", "error", 3000);
      }
    } else {
      this.openPublishModal();
    }
  };

  // Function to open the modal when publishing
  openPublishModal = () => {
    // Logic to open the modal (trigger Bootstrap modal)
    const publishModal = new window.bootstrap.Modal(
      document.getElementById("publishModal")
    );
    publishModal.show();
  };

  confirmPublish = async () => {
    try {
      if (this.state.tags.length < 1) {
        showNotification("You must include at least one tag", "error", 3000);
        return;
      }

      const blogData = {
        title: this.state.title,
        author: this.state.author,
        content: this.state.content,
        excerpt: this.state.excerpt,
        slug:
          this.state.status === "published"
            ? this.state.slug
            : createSafeURL(this.state.title),
        coverPhoto: this.state.coverPhoto,
        photos: this.state.images,
        createdAt: this.state.createdAt || null,
        tags: this.state.tags,
      };

      const blogId = await publishBlog(this.state.blogId, blogData);

      this.setState({ blogId, isPublishing: false });

      if (this.state.status !== "published") {
        const deleted = clearAllBlogCache();
        localStorage.removeItem(this.state.cacheKey);
        showNotification(
          `Cleared ${deleted.length} cache item(s)`,
          "success",
          3000
        );
        showNotification("Blog published successfully!", "success", 3000);
      } else {
        showNotification("Blog updated successfully!", "success", 3000);
      }

      const publishModal = window.bootstrap.Modal.getInstance(
        document.getElementById("publishModal")
      );
      publishModal.hide();
      await this.updateNewTags();
      setTimeout(() => {
        this.props.navigate("/dashboard");
      }, 1000);
    } catch (error) {
      console.error("Error publishing blog:", error);
      showNotification("An error occurred during publishing!", "error", 3000);
      this.setState({ isPublishing: false });
    }
  };

  updateNewTags = async () => {
    const db = getFirestore();
    const tagRef = doc(db, "tags", "all_tags");

    const tagSnap = await getDoc(tagRef);
    let existingTags = [];

    if (tagSnap.exists()) {
      existingTags = tagSnap.data().tags || [];
    }

    const mergedTags = Array.from(
      new Set([...existingTags, ...this.state.tags.map((t) => t.toLowerCase())])
    );

    await setDoc(tagRef, { tags: mergedTags });
  };

  handleAddTag = (value) => {
    const trimmed = value.trim().toLowerCase();
    if (
      trimmed &&
      !this.state.tags.includes(trimmed) &&
      this.state.tags.length < 5
    ) {
      this.setState({ tags: [...this.state.tags, trimmed] });
    }
  };

  handleRemoveTag = (index) => {
    const updated = [...this.state.tags];
    updated.splice(index, 1);
    this.setState({ tags: updated });
  };

  render() {
    return (
      <div
        className="container-fluid"
        style={{
          background: themeLarge.colors.bg,
          fontFamily: "Kumbh Sans",
          marginTop: "85px",
        }}
      >
        <div>
          {/* Page Title */}
          <div
            className="row"
            style={{
              paddingTop: "20px",
            }}
          >
            <div className="col-md-6">
              <p
                style={{
                  fontSize: themeLarge.fontSize.h2,
                  fontFamily: "Noto Sans",
                  color: themeLarge.colors.primary,
                }}
              >
                Write Something New
              </p>
            </div>

            <LastSavedStatus
              updatedAt={this.state.updatedAt}
              theme={themeLarge}
            />
          </div>

          {/* Blog Title */}
          <BlogMetadataForm
            title={this.state.title}
            slug={this.state.slug}
            photos={this.state.images}
            excerpt={this.state.excerpt}
            onTitleChange={this.handleInputChange}
            onSlugChange={(e) => this.setState({ slug: e.target.value })}
            onExcerptChange={(e) => this.setState({ excerpt: e.target.value })}
          />

          <BlogEditor
            content={this.state.content}
            onChange={this.handleContentChange}
            images={this.state.images}
            setImages={(newImages) =>
              this.setState({ images: newImages, hasChanges: true })
            }
          />

          {/* Buttons */}
          <SavePublishButtons
            hasChanges={this.state.hasChanges}
            title={this.state.title}
            content={this.state.content}
            status={this.state.status}
            onSaveDraft={() => this.handleSave("draft")}
            onPublishRequest={() => this.handleSave("published")}
            cacheKey={this.state.cacheKey}
            theme={themeLarge}
            isPublished={this.state.status === "published"}
          />

          {/* Confirm Publish Modal */}
          <PublishModal
            slug={this.state.slug}
            excerpt={this.state.excerpt}
            coverPhoto={this.state.coverPhoto}
            isPublishing={this.state.isPublishing}
            tags={this.state.tags}
            tagSuggestions={this.state.allTags}
            onTagsChange={this.handleAddTag}
            onRemoveTag={this.handleRemoveTag}
            onSlugChange={(e) => this.setState({ slug: e.target.value })}
            onExcerptChange={(e) => this.setState({ excerpt: e.target.value })}
            onCoverPhotoUpload={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              try {
                // const compressed = await compressImage(file);
                const url = await uploadImageToStorage(
                  file,
                  "coverPhotos"
                );
                this.setState({ coverPhoto: url });
                showNotification("Cover photo uploaded!", "success", 2000);
              } catch (err) {
                console.error("Cover photo upload failed:", err);
                showNotification(
                  "Failed to upload cover photo!",
                  "error",
                  3000
                );
              }
            }}
            onConfirm={this.confirmPublish}
          />
        </div>
      </div>
    );
  }
}

function withNavigate(Component) {
  return function Wrapper(props) {
    const navigate = useNavigate();
    return <Component {...props} navigate={navigate} />;
  };
}

export default withNavigate(CreateBlogPage);
